import {AssetQualityLevel} from "@nolimitcity/slot-game/bin/asset/Asset";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Device} from "@nolimitcity/slot-launcher/bin/interfaces/APIOptions";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {NolimitConfig} from "@nolimitcity/slot-launcher/bin/settings/NolimitConfig";
import {SlotApiAdapter} from "./SlotApiAdapter";
import {JurisdictionNames} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/GameClientConfiguration";
import { SlotKeypad } from "@nolimitcity/slot-keypad/bin/SlotKeypad";
import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { GameMode } from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import { Logger } from "@nolimitcity/slot-launcher/bin/utils/Logger";

export enum AssetsQualitySetting {
    NONE = 0,
    LOW,
    MEDIUM,
    HIGH
}

export class GameSetting extends SlotApiAdapter {

    public static readonly DEFAULT_DEV_MODE: boolean = false;
    private static _quality: AssetsQualitySetting;
    private static _smartLoading: boolean;
    private static _replayMode: boolean;
    private static _instance: GameSetting;
    private static _betType: string;

    private constructor(api: ApiPlugin) {
        super(api);

        GameSetting._quality = GameSetting.getQualitySetting(this.getQuality());
        GameSetting._smartLoading = this.getSmartLoading();
        GameSetting._replayMode = this.getReplayMode();
        api.events.on(APIEvent.BET, (data: any) => {
            GameSetting._betType = data.featureName;
        });
        if (GameSetting.isDevMode) {
            console.log(SlotGame.apiPlugin.gameClientConfiguration);
        }
    }

    public static getInstance(api: ApiPlugin): GameSetting {
        if (!GameSetting._instance) {
            GameSetting._instance = new GameSetting(api);
        }
        return GameSetting._instance;
    }

    private static getQualitySetting(value: string): AssetsQualitySetting {
        switch (value) {
            case "high":
                return AssetsQualitySetting.HIGH;
            case "medium":
                return AssetsQualitySetting.MEDIUM;
            case "low":
                return AssetsQualitySetting.LOW;
            default:
                return AssetsQualitySetting.NONE;
        }
    }

    public static get betType(): string {
        return this._betType;
    }

    public static get isBoostedBet(): boolean {
        return SlotKeypad.apiPlugIn.betFeatureController.getActiveBetFeature()?.name === 'BOOSTED_BET';
    }

    public static get isSuperBuy(): boolean {
        return this.isBonusBet && GameSetting.betType === "BOOSTED_BET";
    }

    public static get isBonusBuy(): boolean {
        return this.isBonusBet && (GameSetting.betType === GameMode.FREESPIN || GameSetting.betType === GameMode.FREESPIN_SUPER);
    }

    public static get JuriGFXCensorSettings(): boolean {
        return SlotGame.apiPlugin.gameClientConfiguration.useCensoredGfx;
    }

    public static get JuriGFXUSCensorSettings(): boolean {
        return SlotGame.apiPlugin.gameClientConfiguration.useCensoredGfxUS;
    }

    public static get showMaxWinWatermark(): boolean {
        const config = SlotGame.apiPlugin.gameClientConfiguration;
        return config.showMaxWinProbabilityWatermark;
    }

    public static get showLowProbabilityGfx(): boolean {
        const config = SlotGame.apiPlugin.gameClientConfiguration;
        return config.showLowProbabilityGfx;
    }

    public static get showAllIndiviWins(): boolean {
        const displayWins = SlotGame.apiPlugin.gameClientConfiguration.displayIndividualWinsInGame;
        if (displayWins === undefined) {
            Logger.warn("showAllIndiviWins: not using correctly");
            return false;
        }
        return displayWins;
    }

    public static get isBonusBet(): boolean {
        return this.betType !== "BOOSTED_BET" && !!this.betType;
    }

    public static get quality(): AssetsQualitySetting {
        return this._quality;
    }

    public static get smartLoading(): boolean {
        return this._smartLoading;
    }

    public static get replayMode(): boolean {
        return this._replayMode;
    }

    public static get isDevMode(): boolean {
        return NolimitConfig.isDevMode;
    }

    public static get isPortugal(): boolean {
        return SlotGame.apiPlugin.gameClientConfiguration.jurisdictionName === JurisdictionNames.PORTUGAL;
    }

    public static get isFastSpin(): boolean {
        return this._instance?.isFastSpin() ?? false;
    }

    public static get isSoundEffectsOn(): boolean {
        return this._instance?.isSoundEffectsOn() ?? false;
    }

    public static get isLeftHanded(): boolean {
        return this._instance?.isLeftHanded() ?? false;
    }

    public static getDefaultQualities(): AssetQualityLevel[] {
        const deviceType = NolimitApplication.apiPlugin.options.device;
        if (!this.smartLoading) {
            return this.quality === AssetsQualitySetting.HIGH ? [AssetQualityLevel.HIGH] :
                   this.quality === AssetsQualitySetting.MEDIUM ? [AssetQualityLevel.MEDIUM] :
                   this.quality === AssetsQualitySetting.LOW ? [AssetQualityLevel.LOW] :
                   deviceType === Device.MOBILE ? [AssetQualityLevel.MEDIUM] : [AssetQualityLevel.HIGH];
        }
        return this.quality === AssetsQualitySetting.HIGH ? [AssetQualityLevel.MEDIUM, AssetQualityLevel.HIGH] :
               this.quality === AssetsQualitySetting.MEDIUM ? [AssetQualityLevel.LOW, AssetQualityLevel.MEDIUM] :
               this.quality === AssetsQualitySetting.LOW ? [AssetQualityLevel.LOW] :
               deviceType === Device.MOBILE ? [AssetQualityLevel.LOW, AssetQualityLevel.MEDIUM] : [AssetQualityLevel.MEDIUM, AssetQualityLevel.HIGH];
    }
}